import React from 'react';
import { StyleSheet } from 'react-native';
import { View } from './tw-wrappers';

interface DonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
  children?: React.ReactNode;
  containerBackgroundColor?: string;
}

/**
 * DonutChart - Precise circular progress implementation.
 * Combines outer circular clipping with dual-half masking for 0-100% accuracy.
 */
export const DonutChart = ({
  percentage,
  size = 140,
  strokeWidth = 16,
  color = '#FACC15',
  backgroundColor = '#E5E7EB',
  containerBackgroundColor = '#EFF6FF',
  children,
}: DonutChartProps) => {
  const radius = size / 2;
  const clampedPercentage = Math.min(100, Math.max(0, percentage));
  const totalAngle = clampedPercentage * 3.6;

  /**
   * Renders a rotating slice within a half-width mask.
   * angle: rotation from -180 (hidden) to 0 (full)
   * isRight: which half of the donut we are rendering
   */
  const renderHalfSlice = (angle: number, isRight: boolean) => (
    <View
      style={{
        position: 'absolute',
        width: radius,
        height: size,
        left: isRight ? radius : 0,
        overflow: 'hidden', // Clips the rotating slice to this half
      }}
    >
      <View
        style={{
          width: size,
          height: size,
          left: isRight ? -radius : 0,
          transform: [{ rotate: `${angle}deg` }],
        }}
      >
        <View
          style={{
            width: radius,
            height: size,
            left: isRight ? radius : 0,
            backgroundColor: color,
          }}
        />
      </View>
    </View>
  );

  return (
    <View style={[styles.container, { width: size, height: size }]}>
      {/* 1. OUTER CIRCLE MASK - Enforces the circular shape */}
      <View
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          overflow: 'hidden',
          backgroundColor: backgroundColor,
        }}
      >
        {/* Right Half: Handles 0% to 50% (0 to 180 degrees) */}
        {renderHalfSlice(Math.min(totalAngle, 180) - 180, true)}

        {/* Left Half: Handles 50% to 100% (180 to 360 degrees) */}
        {totalAngle > 180 && renderHalfSlice(totalAngle - 180 - 180, false)}
      </View>

      {/* 2. INNER HOLE - Hides the center of the slices to create the donut */}
      <View
        style={[
          styles.innerCircle,
          {
            width: size - strokeWidth * 2,
            height: size - strokeWidth * 2,
            borderRadius: radius - strokeWidth,
            backgroundColor: containerBackgroundColor,
          },
        ]}
      >
        {children}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  innerCircle: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
});






